/**
 * Bandeau vidéo djassa hub.
 * - Ne télécharge que la vidéo adaptée à l'écran : mobile (< 750 px), ordinateur 720p,
 *   ou ordinateur HD quand l'écran affiche au moins 1800 pixels physiques de large.
 * - Démarre seulement quand le bandeau est visible, se met en pause hors écran ou onglet caché.
 * - Respecte « réduire les animations » : l'affiche fixe reste seule, aucune vidéo chargée.
 * - Bouton pause / lecture pour les vidéos de plus de 5 secondes (WCAG 2.2.2).
 */
class DjassaVideoBanner extends HTMLElement {
  #abort = new AbortController();
  #observer = null;
  #visible = false;
  #userPaused = false;

  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('.dj-vb__toggle');
    if (!this.video) return;

    this.wide = window.matchMedia('(min-width: 750px)');
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    const { signal } = this.#abort;
    this.wide.addEventListener('change', () => this.#load(), { signal });
    this.reduce.addEventListener('change', () => this.#load(), { signal });
    document.addEventListener('visibilitychange', () => this.#sync(), { signal });
    this.video.addEventListener('playing', () => this.classList.add('is-playing'), { signal });
    this.toggle?.addEventListener('click', () => this.#onToggle(), { signal });

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#visible = entry.isIntersecting;
        if (this.#visible && !this.video.dataset.current) this.#load();
        else this.#sync();
      },
      { rootMargin: '200px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
  }

  #load() {
    const src = this.reduce.matches ? '' : this.wide.matches ? this.#desktopSource() : this.dataset.srcMobile;

    if (!src) {
      this.#stop();
      return;
    }
    if (this.video.dataset.current === src) {
      this.#sync();
      return;
    }

    this.classList.remove('is-playing');
    this.video.dataset.current = src;
    this.video.preload = 'auto';
    this.video.src = src;
    if (this.toggle) this.toggle.hidden = false;
    this.#sync();
  }

  #desktopSource() {
    const { srcDesktop, srcDesktopHd } = this.dataset;
    const devicePixels = this.getBoundingClientRect().width * (window.devicePixelRatio || 1);
    return srcDesktopHd && devicePixels >= 1800 ? srcDesktopHd : srcDesktop || srcDesktopHd;
  }

  #stop() {
    this.video.pause();
    this.video.removeAttribute('src');
    delete this.video.dataset.current;
    this.video.load();
    this.classList.remove('is-playing', 'is-paused');
    if (this.toggle) this.toggle.hidden = true;
  }

  #sync() {
    if (!this.video.dataset.current) return;

    const shouldPlay = this.#visible && !document.hidden && !this.#userPaused;
    if (shouldPlay) {
      // Lecture automatique refusée (mode économie d'énergie, etc.) : l'affiche reste visible.
      this.video.play().catch(() => this.classList.remove('is-playing'));
    } else {
      this.video.pause();
    }

    this.classList.toggle('is-paused', this.#userPaused);
    this.toggle?.setAttribute('aria-label', this.#userPaused ? this.dataset.labelPlay : this.dataset.labelPause);
  }

  #onToggle() {
    this.#userPaused = !this.#userPaused;
    this.#sync();
  }
}

if (!customElements.get('djassa-video-banner')) {
  customElements.define('djassa-video-banner', DjassaVideoBanner);
}
